import {
  AloftPrice,
  CompType,
  formatLongRevisionType,
  noop,
  orUndefined,
  schAloftPrice,
} from '@aloft/toolkit-shared';
import {
  Block,
  Flex,
  InfoIcon,
  RadioGroup,
  SimpleDollarInput,
  Tabs,
  Typography,
  colors,
} from '@aloft/toolkit-sui';

import { useEffect, useMemo, useRef, useState } from 'react';
import { Controller, Form, FormProvider, SubmitHandler, useWatch } from 'react-hook-form';

import { FormTextarea } from '../../components/FormTextarea/FormTextarea.tsx';
import { useTkv2ReferenceBarEvents } from '../../components/ReferenceBar/useTkv2ReferenceBarEvents.ts';
import { ApproachesToValueChart } from '../../components/graphs/reconciliation/ApproachesToValueChart.tsx';
import { ValidationErrorType } from '../../hooks/validation/types.ts';
import { useAppraisifyValidation } from '../../hooks/validation/useAppraisifyValidation.ts';
import { submitReconciliation } from '../../redux/features/reconciliationSlice.ts';
import {
  setReferenceViewOpen,
  setSelectedDataSheet,
} from '../../redux/features/referenceBarUISlice.ts';
import { selectCommentary } from '../../redux/features/selectors/commentarySelectors/selectCommentary.ts';
import { selectOrder } from '../../redux/features/selectors/orderSelectors.ts';
import { selectEstimatedROI } from '../../redux/features/selectors/selectEstimatedROI.ts';
import { selectReconciliation } from '../../redux/features/selectors/selectReconciliation.ts';
import { useAppDispatch, useAppSelector } from '../../redux/hooks.ts';
import { RequiredFieldAnchor } from '../alerts/RequiredFieldAnchor.tsx';
import { useRequiredFieldFocusEffect } from '../alerts/useRequiredFieldFocusEffect.ts';
import { ReconciliationSummarizedValues } from './components/ReconciliationSummarizedValues.tsx';
import { useDesktopReconciliationComparison } from './hooks/useDesktopReconciliationComparison.ts';
import { useReconciliationForm } from './hooks/useReconciliationForm.ts';
import { ReconciliationFormOutput } from './schemas.ts';

const VALUE_TYPE_ITEMS = [
  {
    label: 'As-is',
    value: 'as-is',
  },
  {
    label:
      'Subject to the following repairs or alterations on the basis of a hypothetical condition that the repairs or alterations have been completed',
    value: 'subject-to',
  },
];

const DiscrepancyWarningIcon = () => (
  <Flex
    width="28px"
    height="28px"
    borderRadius="50%"
    alignItems="center"
    justifyContent="center"
    __dangerDoNotUseSx={{
      backgroundColor: '#EE9611',
      flexShrink: 0,
    }}
  >
    <Typography
      __dangerDoNotUseSx={{
        color: '#FFFFFF',
        fontSize: '22px',
        lineHeight: '1',
        fontWeight: 700,
      }}
    >
      !
    </Typography>
  </Flex>
);

type ReconciliationTab = 'opinions' | 'revision' | 'final';

const findActualScrollRoot = (start: HTMLElement | null): HTMLElement | Window => {
  let el: HTMLElement | null = start;
  while (el) {
    const { overflow, overflowY } = window.getComputedStyle(el);
    const scrollable =
      overflowY === 'auto' ||
      overflowY === 'scroll' ||
      overflow === 'auto' ||
      overflow === 'scroll';
    if (scrollable && el.scrollHeight > el.clientHeight + 1) {
      return el;
    }
    el = el.parentElement;
  }
  return window;
};

export const ReconciliationModule = () => {
  useRequiredFieldFocusEffect();
  const dispatch = useAppDispatch();
  const form = useReconciliationForm();
  const [activeTab, setActiveTab] = useState<ReconciliationTab>('opinions');
  const [opinionViewModes, setOpinionViewModes] = useState<{
    default: 'chart' | 'table';
    aiv: 'chart' | 'table';
    arv: 'chart' | 'table';
  }>({
    default: 'chart',
    aiv: 'chart',
    arv: 'chart',
  });
  const { reconciliation: reconciliationCommentary } = useAppSelector(selectCommentary);
  const reconciliation = useAppSelector(selectReconciliation);
  const order = useAppSelector(selectOrder);
  const {
    isRevision,
    revisionData,
    previousRevisionsData,
    hasArvOption: isArvOrder,
    isMock: isMockOrder,
  } = order;
  const shouldShowRevisionTab = isRevision || !!revisionData?.revisionType;
  const previousRevisionCommentaryEntries = useMemo(() => {
    const commentaries = reconciliationCommentary?.previousRevisionCommentaries ?? [];
    return commentaries
      .map((commentary, index) => ({
        commentary,
        revisionType: previousRevisionsData?.[index]?.revisionType ?? 'revision',
      }))
      .filter(({ commentary }) => commentary.trim().length > 0)
      .reverse();
  }, [reconciliationCommentary?.previousRevisionCommentaries, previousRevisionsData]);
  const scrollContainerRef = useRef<HTMLDivElement | null>(null);
  const sectionRefs = useRef<Record<ReconciliationTab, HTMLDivElement | null>>({
    opinions: null,
    revision: null,
    final: null,
  });
  const watchedOpinionOfMarketValue = useWatch({
    control: form.control,
    name: 'opinionOfMarketValue',
  });
  const watchedOpinionOfAfterRepairValue = useWatch({
    control: form.control,
    name: 'opinionOfAfterRepairValue',
  });
  const watchedTotalRenovationBudget = useWatch({
    control: form.control,
    name: 'totalRenovationBudget',
  });
  const watchedOpinionOfMarketValuePrice = orUndefined(schAloftPrice.parse)(
    watchedOpinionOfMarketValue,
  );
  const watchedOpinionOfAfterRepairValuePrice = orUndefined(schAloftPrice.parse)(
    watchedOpinionOfAfterRepairValue,
  );
  const watchedTotalRenovationBudgetPrice = orUndefined(schAloftPrice.parse)(
    watchedTotalRenovationBudget,
  );
  const {
    isHybridOrder: isHybridDesktopComparison,
    priorDesktopAiv,
    priorDesktopArv,
    aivDelta,
    arvDelta,
    hasAivDiscrepancy,
    hasArvDiscrepancy,
  } = useDesktopReconciliationComparison({
    opinionOfMarketValue: watchedOpinionOfMarketValuePrice,
    opinionOfAfterRepairValue: watchedOpinionOfAfterRepairValuePrice,
  });
  const revisionCommentaryLabel = `${formatLongRevisionType(
    revisionData?.revisionType || 'rov',
  )} Commentary`;
  const tabs = useMemo(
    () =>
      [
        { label: 'Opinions of Value', value: 'opinions' },
        ...(shouldShowRevisionTab ? [{ label: 'Revision / ROV', value: 'revision' }] : []),
        { label: 'Final Reconciliation', value: 'final' },
      ] as { label: string; value: ReconciliationTab }[],
    [shouldShowRevisionTab],
  );

  useEffect(() => {
    form.reset({
      reconciliationCommentary: reconciliationCommentary?.reconciliationCommentary ?? '',
      highestAndBestUseCommentary: reconciliationCommentary?.highestAndBestUseCommentary ?? '',
      totalCostToCure: orUndefined(schAloftPrice.parse)(reconciliation.totalCostToCure),
      totalCostToCureCommentary: reconciliationCommentary?.totalCostToCureCommentary ?? '',
      opinionOfMarketValue: orUndefined(schAloftPrice.parse)(reconciliation.opinionOfMarketValue),
      opinionOfAfterRepairValue: orUndefined(schAloftPrice.parse)(
        reconciliation.opinionOfAfterRepairValue,
      ),
      totalRenovationBudget: orUndefined(schAloftPrice.parse)(reconciliation.totalRenovationBudget),
      revisionCommentary: reconciliationCommentary?.revisionCommentary ?? '',
      alternativeComparablesCommentary:
        reconciliationCommentary?.alternativeComparablesCommentary ?? [],
      feasibilityCommentary: reconciliationCommentary?.feasibilityCommentary ?? '',
      aivDiscrepancyCommentary: reconciliationCommentary?.aivDiscrepancyCommentary ?? '',
      arvDiscrepancyCommentary: reconciliationCommentary?.arvDiscrepancyCommentary ?? '',
      hasAivDiscrepancy,
      hasArvDiscrepancy,
      valueTypes: reconciliation.valueTypes,
    });
  }, [reconciliationCommentary, reconciliation, hasAivDiscrepancy, hasArvDiscrepancy]);

  useEffect(() => {
    form.setValue('hasAivDiscrepancy', hasAivDiscrepancy);
    form.setValue('hasArvDiscrepancy', hasArvDiscrepancy);
  }, [form, hasAivDiscrepancy, hasArvDiscrepancy]);

  const validation = useAppraisifyValidation();
  const {
    totalCostToCure: totalCostToCureError,
    totalCostToCureCommentary: totalCostToCureCommentaryError,
    opinionOfMarketValue: opinionOfMarketValueError,
    opinionOfAfterRepairValue: opinionOfAfterRepairValueError,
    totalRenovationBudget: totalRenovationBudgetError,
    highestAndBestUseCommentary: highestAndBestUseCommentaryError,
    reconciliationCommentary: reconciliationCommentaryError,
    revisionCommentary: revisionCommentaryError,
    alternativeComparablesCommentary: alternativeComparablesCommentaryError,
    feasibilityCommentary: feasibilityCommentaryError,
    aivDiscrepancyCommentary: aivDiscrepancyCommentaryError,
    arvDiscrepancyCommentary: arvDiscrepancyCommentaryError,
  } = validation.errors.Reconciliation;

  const estimatedROI = isArvOrder ? useAppSelector(selectEstimatedROI) : undefined;

  const shouldShowFeasibilityCommentary = useMemo(() => {
    if (!estimatedROI) return false;
    return estimatedROI.toDollarLike() <= 0;
  }, [estimatedROI]);

  useEffect(() => {
    dispatch(setSelectedDataSheet('pdf'));
    dispatch(setReferenceViewOpen(true));
  }, []);

  const onSubmit: SubmitHandler<ReconciliationFormOutput> = (data) => {
    dispatch(submitReconciliation({ ...data }));
  };

  const handleChange = () => {
    form.handleSubmit(onSubmit, undefined)().catch(noop);
  };

  let roiDisplay = 'TBD';
  if (estimatedROI) {
    roiDisplay =
      estimatedROI.toDollarLike() < 0
        ? `-$${Math.abs(estimatedROI.toDollarLike()).toLocaleString()}`
        : estimatedROI.toString(true);
  }
  let roiDisplayColor = '#3455DB';
  if (estimatedROI) {
    roiDisplayColor = estimatedROI.toDollarLike() < 0 ? '#EA4335' : '#00A67F';
  }

  const formatValue = (value?: AloftPrice) => (value ? value.toString(true) : 'N/A');
  const discrepancyDirection = (delta?: AloftPrice) => {
    if (!delta) return '';
    if (delta.toDollarLike() > 0) return 'increase';
    if (delta.toDollarLike() < 0) return 'decrease';
    return '';
  };
  const discrepancyDifference = (delta?: AloftPrice) => {
    if (!delta) return '';
    return AloftPrice.fromDollarLike(Math.abs(delta.toDollarLike())).toString(true);
  };
  const renderChartOrSummary = (compType?: CompType) => {
    const modeKey = compType ?? 'default';
    const opinionViewMode = opinionViewModes[modeKey];
    const setOpinionViewMode = (mode: 'chart' | 'table') =>
      setOpinionViewModes((prev) => ({ ...prev, [modeKey]: mode }));
    const switcher = (
      <Flex
        __dangerDoNotUseSx={{
          border: '2px solid #3757D6',
          borderRadius: '8px',
          overflow: 'hidden',
          width: 'fit-content',
          backgroundColor: '#ffffff',
        }}
      >
        <button
          type="button"
          onClick={() => setOpinionViewMode('chart')}
          style={{
            border: 'none',
            borderRight: '1px solid #3757D6',
            backgroundColor: opinionViewMode === 'chart' ? '#BCC8F8' : '#FFFFFF',
            color: '#3757D6',
            fontWeight: 700,
            fontSize: '14px',
            lineHeight: '20px',
            padding: '8px 20px',
            cursor: 'pointer',
          }}
        >
          Chart
        </button>
        <button
          type="button"
          onClick={() => setOpinionViewMode('table')}
          style={{
            border: 'none',
            backgroundColor: opinionViewMode === 'table' ? '#BCC8F8' : '#FFFFFF',
            color: '#3757D6',
            fontWeight: 700,
            fontSize: '14px',
            lineHeight: '24px',
            padding: '4px 10px',
            cursor: 'pointer',
          }}
        >
          Table
        </button>
      </Flex>
    );

    if (opinionViewMode === 'chart') {
      return <ApproachesToValueChart compType={compType} inlineSwitcher={switcher} />;
    }
    return <ReconciliationSummarizedValues compType={compType} inlineSwitcher={switcher} />;
  };

  useEffect(() => {
    const inner = scrollContainerRef.current;
    if (!inner) {
      return undefined;
    }
    const scrollRoot = findActualScrollRoot(inner);

    const handleScroll = () => {
      const containerRect =
        scrollRoot instanceof Window
          ? new DOMRect(0, 0, window.innerWidth, window.innerHeight)
          : scrollRoot.getBoundingClientRect();
      const clientHeight =
        scrollRoot instanceof Window ? window.innerHeight : scrollRoot.clientHeight;
      const scrollHeight =
        scrollRoot instanceof Window
          ? document.documentElement.scrollHeight
          : scrollRoot.scrollHeight;
      const scrollTop = scrollRoot instanceof Window ? window.scrollY : scrollRoot.scrollTop;
      const isAtBottom = scrollHeight - scrollTop - clientHeight <= 1;

      if (isAtBottom) {
        const lastTab = tabs[tabs.length - 1];
        if (lastTab) {
          setActiveTab(lastTab.value);
        }
        return;
      }

      const entries = tabs
        .map(({ value }) => ({
          tab: value,
          el: sectionRefs.current[value],
        }))
        .filter((entry): entry is { tab: ReconciliationTab; el: HTMLDivElement } => !!entry.el)
        .map(({ tab, el }) => ({
          tab,
          rect: el.getBoundingClientRect(),
        }))
        .filter(({ rect }) => rect.top + rect.height / 4 >= containerRect.top)
        .map(({ tab, rect }) => ({
          tab,
          distance: Math.abs(rect.top - containerRect.top),
        }))
        .sort((a, b) => a.distance - b.distance);
      const nextActive = entries[0];
      if (nextActive) {
        setActiveTab(nextActive.tab);
      }
    };

    scrollRoot.addEventListener('scroll', handleScroll, { passive: true });
    return () => scrollRoot.removeEventListener('scroll', handleScroll);
  }, [tabs]);

  const handleTabOnClick = (tab: ReconciliationTab) => {
    setActiveTab(tab);
    sectionRefs.current[tab]?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  return (
    <Flex {...useTkv2ReferenceBarEvents()} width="100%">
      <FormProvider {...form}>
        <Form style={{ width: '100%' }} onChange={handleChange}>
          <Flex
            margin="15px"
            flexDirection="column"
            gap={4}
            flex={1}
            width="95%"
            minWidth="600px"
            maxWidth="900px"
            paddingBottom="50px"
          >
            <Flex
              position="sticky"
              top={0}
              zIndex={3}
              backgroundColor="surface1"
              paddingTop="4px"
              paddingBottom="4px"
              flexDirection="column"
              gap={2}
            >
              <Typography variant="h1">Reconciliation</Typography>
              <Tabs
                tabs={tabs as any}
                activeTab={activeTab}
                setActiveTab={handleTabOnClick as any}
              />
            </Flex>
            <Flex
              ref={scrollContainerRef}
              flexDirection="column"
              gap={4}
              overflow="auto"
              minHeight={0}
            >
              <Flex
                ref={(el) => {
                  sectionRefs.current.opinions = el;
                }}
                backgroundColor={activeTab === 'opinions' ? 'surface3' : 'surface1'}
                borderRadius="5px"
                padding="12px"
                flexDirection="column"
                gap={4}
              >
                {!isArvOrder && reconciliation?.includeTotalCostToCure && (
                  <Flex flexDirection="column">
                    <Flex flex={1} justifyContent="space-between" alignItems="center">
                      <Flex alignItems="center" gap="4px">
                        <Typography variant="h3" color="textSecondary">
                          Total Cost to Cure
                          <span className="simple-field-required" style={{ marginLeft: '2px' }}>
                            {!isMockOrder && '*'}
                          </span>
                        </Typography>
                        <InfoIcon tooltipContent="Major items only (i.e. soundness, security, livability). No cosmetic or minor deferred maintenance." />
                      </Flex>
                      <Controller
                        name="totalCostToCure"
                        control={form.control}
                        render={({ field, fieldState }) => (
                          <RequiredFieldAnchor
                            fieldName="totalCostToCure"
                            isFieldComplete={field.value != null}
                          >
                            <SimpleDollarInput
                              value={field.value ? field.value.toString(true, true) : ''}
                              onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                                const value = e.target.value
                                  ? AloftPrice.fromDollarLike(e.target.value)
                                  : undefined;
                                field.onChange(value);
                                handleChange();
                              }}
                              onBlur={field.onBlur}
                              required={!isMockOrder}
                              placeholder={
                                isMockOrder
                                  ? 'Optional'
                                  : 'Required. Please describe the major items (i.e. soundness, security, livability) that account for this cost to cure estimation. Do not include cosmetic or minor deferred maintenance.'
                              }
                              width="300px"
                              errorMessage={
                                fieldState.isTouched ? totalCostToCureError?.message : undefined
                              }
                              invalid={fieldState.invalid && fieldState.isTouched}
                            />
                          </RequiredFieldAnchor>
                        )}
                      />
                    </Flex>
                    <Controller
                      name="totalCostToCureCommentary"
                      control={form.control}
                      render={({ fieldState }) => (
                        <FormTextarea
                          name="totalCostToCureCommentary"
                          requiredFieldName="totalCostToCureCommentary"
                          control={form.control}
                          placeholder={`${!isMockOrder ? 'Required' : 'Optional'}. Please describe the major items (i.e. soundness, security, livability) that account for this cost to cure estimation. Do not include cosmetic or minor deferred maintenance.`}
                          isRequired={!isMockOrder}
                          labelWidth="250px"
                          inputWidth="500px"
                          height="200px"
                          labelColor="textSecondary"
                          size="sm"
                          resize="vertical"
                          customErrorMessage={
                            fieldState.isTouched
                              ? totalCostToCureCommentaryError?.message
                              : undefined
                          }
                          column
                        />
                      )}
                    />
                  </Flex>
                )}
                {isArvOrder && (
                  <Flex flex={1} justifyContent="space-between" alignItems="center">
                    <Block>
                      <Typography variant="h3" color="textSecondary">
                        Renovation Budget
                        <span className="simple-field-required" style={{ marginLeft: '2px' }}>
                          {!isMockOrder && '*'}
                        </span>
                      </Typography>
                    </Block>
                    <Controller
                      name="totalRenovationBudget"
                      control={form.control}
                      render={({ field, fieldState }) => (
                        <RequiredFieldAnchor
                          fieldName="totalRenovationBudget"
                          isFieldComplete={field.value != null}
                        >
                          <SimpleDollarInput
                            value={field.value ? field.value.toString(true, true) : ''}
                            onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                              const value = e.target.value
                                ? AloftPrice.fromDollarLike(e.target.value)
                                : undefined;
                              field.onChange(value);
                              handleChange();
                            }}
                            onBlur={field.onBlur}
                            required={!isMockOrder}
                            placeholder={isMockOrder ? 'Optional' : 'Required'}
                            width="300px"
                            errorMessage={
                              fieldState.isTouched ? totalRenovationBudgetError?.message : undefined
                            }
                            invalid={fieldState.invalid && fieldState.isTouched}
                          />
                        </RequiredFieldAnchor>
                      )}
                    />
                  </Flex>
                )}
                {!isArvOrder && (
                  <Flex flex={1} justifyContent="space-between" alignItems="center">
                    <Block>
                      <Typography variant="h3" color="textSecondary">
                        Opinion of Market Value
                        <span className="simple-field-required" style={{ marginLeft: '2px' }}>
                          {!isMockOrder && '*'}
                        </span>
                      </Typography>
                      {priorDesktopAiv && (
                        <Typography variant="body2" color="textSecondary">
                          Previous Desktop Value: {formatValue(priorDesktopAiv)}
                        </Typography>
                      )}
                    </Block>
                    <Controller
                      name="opinionOfMarketValue"
                      control={form.control}
                      render={({ field }) => (
                        <RequiredFieldAnchor
                          fieldName="opinionOfMarketValue"
                          isFieldComplete={field.value != null}
                        >
                          <SimpleDollarInput
                            value={field.value ? field.value.toString(true, true) : ''}
                            onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                              const value = e.target.value
                                ? AloftPrice.fromDollarLike(e.target.value)
                                : undefined;
                              field.onChange(value);
                              handleChange();
                            }}
                            onBlur={field.onBlur}
                            required={!isMockOrder}
                            placeholder={isMockOrder ? 'Optional' : 'Required'}
                            width="300px"
                            errorMessage={
                              opinionOfMarketValueError?.type === ValidationErrorType.INVALID
                                ? opinionOfMarketValueError?.message
                                : ''
                            }
                          />
                        </RequiredFieldAnchor>
                      )}
                    />
                  </Flex>
                )}
                {!isArvOrder && renderChartOrSummary()}
                {!isArvOrder && hasAivDiscrepancy && (
                  <Flex flexDirection="column" gap={3}>
                    <Flex alignItems="center" gap={2}>
                      <DiscrepancyWarningIcon />
                      <Typography
                        variant="h5"
                        __dangerDoNotUseSx={{ color: '#072138', lineHeight: '100%' }}
                      >
                        Your Opinion of Market Value differs between this current Hybrid appraisal
                        and the previous Desktop Appraisal. Explain the difference in the Value
                        Discrepancy Commentary below.
                      </Typography>
                    </Flex>
                    <Controller
                      name="aivDiscrepancyCommentary"
                      control={form.control}
                      render={({ fieldState }) => (
                        <FormTextarea
                          name="aivDiscrepancyCommentary"
                          requiredFieldName="aivDiscrepancyCommentary"
                          control={form.control}
                          label="Value Discrepancy Commentary"
                          placeholder={
                            isMockOrder
                              ? 'Optional'
                              : 'Please acknowledge the prior Desktop service and evaluate the impact of the interior inspection on the previous value conclusion. Clearly document any material discrepancies found during the site visit and their subsequent effect on the appraised value.'
                          }
                          isRequired={!isMockOrder}
                          labelWidth="250px"
                          inputWidth="500px"
                          labelColor="textSecondary"
                          labelVariant="h3"
                          size="sm"
                          resize="vertical"
                          column
                          customErrorMessage={
                            fieldState.isTouched
                              ? aivDiscrepancyCommentaryError?.message
                              : undefined
                          }
                        />
                      )}
                    />
                  </Flex>
                )}
                {isArvOrder && (
                  <>
                    <Flex flex={1} justifyContent="space-between" alignItems="center">
                      <Block>
                        <Flex alignItems="center" gap={2}>
                          <Typography variant="h3" color="textSecondary">
                            Opinion of As-Is Market Value
                            <span className="simple-field-required" style={{ marginLeft: '2px' }}>
                              {!isMockOrder && '*'}
                            </span>
                          </Typography>
                          <Typography
                            variant="body1"
                            color={colors.aivText as any}
                            __dangerDoNotUseSx={{
                              textTransform: 'uppercase',
                              backgroundColor: colors.aiv,
                              padding: '4px 8px',
                              borderRadius: '4px',
                              fontSize: '11px',
                              fontWeight: 700,
                              lineHeight: '17px',
                            }}
                          >
                            AIV
                          </Typography>
                        </Flex>
                        {isHybridDesktopComparison && priorDesktopAiv && (
                          <Typography variant="body2" color="textSecondary">
                            Previous Desktop AIV: {formatValue(priorDesktopAiv)}
                          </Typography>
                        )}
                      </Block>
                      <Controller
                        name="opinionOfMarketValue"
                        control={form.control}
                        render={({ field }) => (
                          <RequiredFieldAnchor
                            fieldName="opinionOfMarketValue"
                            isFieldComplete={field.value != null}
                          >
                            <SimpleDollarInput
                              value={field.value ? field.value.toString(true, true) : ''}
                              onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                                const trimmedValue = e.target.value.trim();
                                const value = trimmedValue
                                  ? AloftPrice.fromDollarLike(trimmedValue)
                                  : undefined;
                                field.onChange(value);
                                handleChange();
                              }}
                              onBlur={field.onBlur}
                              required
                              placeholder="Required"
                              width="300px"
                              errorMessage={
                                opinionOfMarketValueError?.type === ValidationErrorType.INVALID
                                  ? opinionOfMarketValueError?.message
                                  : ''
                              }
                            />
                          </RequiredFieldAnchor>
                        )}
                      />
                    </Flex>
                    {renderChartOrSummary('aiv')}
                    {hasAivDiscrepancy && (
                      <Flex flexDirection="column" gap={3}>
                        <Flex alignItems="center" gap={2}>
                          <DiscrepancyWarningIcon />
                          <Typography
                            variant="h5"
                            __dangerDoNotUseSx={{ color: '#072138', lineHeight: '100%' }}
                          >
                            Previous Desktop AIV was {formatValue(priorDesktopAiv)} and Hybrid AIV
                            is {formatValue(watchedOpinionOfMarketValuePrice)}. This is a{' '}
                            {discrepancyDifference(aivDelta)} {discrepancyDirection(aivDelta)}.
                            Provide detailed commentary explaining the difference.
                          </Typography>
                        </Flex>
                        <Controller
                          name="aivDiscrepancyCommentary"
                          control={form.control}
                          render={({ fieldState }) => (
                            <FormTextarea
                              name="aivDiscrepancyCommentary"
                              requiredFieldName="aivDiscrepancyCommentary"
                              control={form.control}
                              label="AIV Discrepancy Commentary"
                              placeholder={
                                isMockOrder
                                  ? 'Optional'
                                  : 'Please acknowledge the prior Desktop service and evaluate the impact of the interior inspection on the previous value conclusion. Clearly document any material discrepancies found during the site visit and their subsequent effect on the appraised value.'
                              }
                              isRequired={!isMockOrder}
                              labelWidth="250px"
                              inputWidth="500px"
                              labelColor="textSecondary"
                              labelVariant="h3"
                              size="sm"
                              resize="vertical"
                              column
                              customErrorMessage={
                                fieldState.isTouched
                                  ? aivDiscrepancyCommentaryError?.message
                                  : undefined
                              }
                            />
                          )}
                        />
                      </Flex>
                    )}
                    <Flex flex={1} justifyContent="space-between" alignItems="center">
                      <Block>
                        <Flex alignItems="center" gap={2}>
                          <Typography variant="h3" color="textSecondary">
                            Opinion of After-Repair Market Value
                            <span className="simple-field-required" style={{ marginLeft: '2px' }}>
                              {!isMockOrder && '*'}
                            </span>
                          </Typography>
                          <Typography
                            variant="body1"
                            color="textOffset"
                            __dangerDoNotUseSx={{
                              textTransform: 'uppercase',
                              backgroundColor: colors.arv,
                              padding: '4px 8px',
                              borderRadius: '4px',
                              fontSize: '11px',
                              fontWeight: 700,
                              lineHeight: '17px',
                            }}
                          >
                            ARV
                          </Typography>
                        </Flex>
                        {isHybridDesktopComparison && priorDesktopArv && (
                          <Typography variant="body2" color="textSecondary">
                            Previous Desktop ARV: {formatValue(priorDesktopArv)}
                          </Typography>
                        )}
                      </Block>
                      <Controller
                        name="opinionOfAfterRepairValue"
                        control={form.control}
                        render={({ field }) => (
                          <RequiredFieldAnchor
                            fieldName="opinionOfAfterRepairValue"
                            isFieldComplete={field.value != null}
                          >
                            <SimpleDollarInput
                              value={field.value ? field.value.toString(true, true) : ''}
                              onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                                const trimmedValue = e.target.value.trim();
                                const value = trimmedValue
                                  ? AloftPrice.fromDollarLike(trimmedValue)
                                  : undefined;
                                field.onChange(value);
                                handleChange();
                              }}
                              onBlur={field.onBlur}
                              required
                              placeholder="Required"
                              width="300px"
                              errorMessage={
                                opinionOfAfterRepairValueError?.type === ValidationErrorType.INVALID
                                  ? opinionOfAfterRepairValueError?.message
                                  : ''
                              }
                            />
                          </RequiredFieldAnchor>
                        )}
                      />
                    </Flex>
                    {renderChartOrSummary('arv')}
                    {hasArvDiscrepancy && (
                      <Flex flexDirection="column" gap={3}>
                        <Flex alignItems="center" gap={2}>
                          <DiscrepancyWarningIcon />
                          <Typography
                            variant="h5"
                            __dangerDoNotUseSx={{ color: '#072138', lineHeight: '100%' }}
                          >
                            Previous Desktop ARV was {formatValue(priorDesktopArv)} and Hybrid ARV
                            is {formatValue(watchedOpinionOfAfterRepairValuePrice)}. This is a{' '}
                            {discrepancyDifference(arvDelta)} {discrepancyDirection(arvDelta)}.
                            Provide detailed commentary explaining the difference.
                          </Typography>
                        </Flex>
                        <Controller
                          name="arvDiscrepancyCommentary"
                          control={form.control}
                          render={({ fieldState }) => (
                            <FormTextarea
                              name="arvDiscrepancyCommentary"
                              requiredFieldName="arvDiscrepancyCommentary"
                              control={form.control}
                              label="ARV Discrepancy Commentary"
                              placeholder={
                                isMockOrder
                                  ? 'Optional'
                                  : 'Please acknowledge the prior Desktop service and evaluate the impact of the interior inspection on the previous value conclusion. Clearly document any material discrepancies found during the site visit and their subsequent effect on the appraised value.'
                              }
                              isRequired={!isMockOrder}
                              labelWidth="250px"
                              inputWidth="500px"
                              labelColor="textSecondary"
                              labelVariant="h3"
                              size="sm"
                              resize="vertical"
                              column
                              customErrorMessage={
                                fieldState.isTouched
                                  ? arvDiscrepancyCommentaryError?.message
                                  : undefined
                              }
                            />
                          )}
                        />
                      </Flex>
                    )}
                    <Flex flex={1} justifyContent="space-between" alignItems="center">
                      <Block>
                        <Typography variant="h3" __dangerDoNotUseSx={{ color: '#465C72' }}>
                          Estimated Return on Investment
                        </Typography>
                        <Typography variant="body2" __dangerDoNotUseSx={{ color: '#465C72' }}>
                          {'\u2248 After-Repair Value - (Renovation Budget + As-Is Value)'}
                        </Typography>
                      </Block>
                      <Flex flexDirection="column" alignItems="flex-end" justifyContent="center">
                        <Typography variant="body1" __dangerDoNotUseSx={{ color: roiDisplayColor }}>
                          {roiDisplay}
                        </Typography>
                        <Typography variant="body2" __dangerDoNotUseSx={{ color: '#465C72' }}>
                          {`\u2248 ${formatValue(watchedOpinionOfAfterRepairValuePrice)} - (${formatValue(
                            watchedTotalRenovationBudgetPrice,
                          )} + ${formatValue(watchedOpinionOfMarketValuePrice)})`}
                        </Typography>
                      </Flex>
                    </Flex>
                    {shouldShowFeasibilityCommentary && (
                      <Flex flex={1} flexDirection="column">
                        <Controller
                          name="feasibilityCommentary"
                          control={form.control}
                          render={({ fieldState }) => (
                            <FormTextarea
                              name="feasibilityCommentary"
                              requiredFieldName="feasibilityCommentary"
                              control={form.control}
                              label="Feasibility Commentary"
                              placeholder={
                                isMockOrder
                                  ? 'Optional'
                                  : 'Required. Provide a narrative explanation for why you believe the ROI on this property is negative. Reminder that the AIV reflects current conditions, while the ARV reflects post-repair conditions.'
                              }
                              isRequired={!isMockOrder}
                              labelWidth="250px"
                              inputWidth="500px"
                              height="200px"
                              labelColor="textSecondary"
                              labelVariant="h3"
                              size="sm"
                              resize="vertical"
                              customErrorMessage={
                                fieldState.isTouched
                                  ? feasibilityCommentaryError?.message
                                  : undefined
                              }
                              column
                            />
                          )}
                        />
                      </Flex>
                    )}
                  </>
                )}
              </Flex>
              {shouldShowRevisionTab && (
                <>
                  <Typography variant="h1">Revision / ROV</Typography>
                  <Flex
                    ref={(el) => {
                      sectionRefs.current.revision = el;
                    }}
                    backgroundColor={activeTab === 'revision' ? 'surface3' : 'surface1'}
                    borderRadius="5px"
                    padding="12px"
                    flexDirection="column"
                    gap={4}
                  >
                    {previousRevisionCommentaryEntries.map(
                      ({ commentary, revisionType }, index) => (
                        <Flex
                          key={`previous-revision-commentary-${index}`}
                          flex={1}
                          flexDirection="column"
                          gap={1}
                        >
                          <Typography variant="h3" color="textSecondary">
                            {`Previous ${formatLongRevisionType(revisionType)} Commentary`}
                          </Typography>
                          <Typography
                            variant="body1"
                            color="textSecondary"
                            __dangerDoNotUseSx={{ whiteSpace: 'pre-wrap' }}
                          >
                            {commentary}
                          </Typography>
                        </Flex>
                      ),
                    )}
                    <Flex flex={1} flexDirection="column">
                      <Flex
                        alignItems="center"
                        gap="5px"
                        marginBottom="5px"
                        padding="8px"
                        borderRadius="5px"
                        borderColor="strokeDefault"
                        borderStyle="solid"
                        borderWidth="1px"
                        backgroundColor="surface6"
                      >
                        <Typography variant="h4" color="textSecondary">
                          Request Description:
                        </Typography>
                        <Typography
                          variant="placeholder"
                          color="textSecondary"
                          __dangerDoNotUseSx={{ whiteSpace: 'pre-wrap' }}
                        >
                          {revisionData?.requestDescription || ''}
                        </Typography>
                      </Flex>
                      <Controller
                        name="revisionCommentary"
                        control={form.control}
                        render={({ fieldState }) => (
                          <FormTextarea
                            name="revisionCommentary"
                            requiredFieldName="revisionCommentary"
                            control={form.control}
                            label={revisionCommentaryLabel}
                            placeholder={isMockOrder ? 'Optional' : 'Required'}
                            isRequired={!isMockOrder}
                            labelWidth="250px"
                            inputWidth="500px"
                            height="200px"
                            labelColor="textSecondary"
                            labelVariant="h3"
                            size="sm"
                            resize="vertical"
                            customErrorMessage={
                              fieldState.isTouched ? revisionCommentaryError?.message : undefined
                            }
                            column
                          />
                        )}
                      />
                    </Flex>
                    {revisionData?.comparables?.map((comp, index) => (
                      <Flex flex={1} flexDirection="column" key={comp.address}>
                        <Controller
                          name={`alternativeComparablesCommentary.${index}` as const}
                          control={form.control}
                          render={({ fieldState }) => (
                            <FormTextarea
                              name={`alternativeComparablesCommentary.${index}` as const}
                              requiredFieldName="alternativeComparablesCommentary"
                              control={form.control}
                              label={`Alternative Comparable ${index + 1}: ${comp.address}`}
                              placeholder={isMockOrder ? 'Optional' : 'Required'}
                              isRequired={!isMockOrder}
                              labelWidth="250px"
                              inputWidth="500px"
                              labelColor="textSecondary"
                              labelVariant="h3"
                              size="sm"
                              resize="vertical"
                              column
                              customErrorMessage={
                                fieldState.isTouched &&
                                reconciliationCommentary?.alternativeComparablesCommentary?.[
                                  index
                                ] === ''
                                  ? alternativeComparablesCommentaryError?.message
                                  : undefined
                              }
                            />
                          )}
                        />
                      </Flex>
                    ))}
                  </Flex>
                </>
              )}
              <Flex
                ref={(el) => {
                  sectionRefs.current.final = el;
                }}
                backgroundColor={activeTab === 'final' ? 'surface3' : 'surface1'}
                borderRadius="5px"
                padding="12px"
                flexDirection="column"
                gap={4}
              >
                <Flex flex={1} flexDirection="column">
                  <Controller
                    name="reconciliationCommentary"
                    control={form.control}
                    render={({ fieldState }) => (
                      <FormTextarea
                        name="reconciliationCommentary"
                        requiredFieldName="reconciliationCommentary"
                        control={form.control}
                        label="Reconciliation Commentary"
                        placeholder={isMockOrder ? 'Optional' : 'Required'}
                        isRequired={!isMockOrder}
                        labelWidth="250px"
                        inputWidth="500px"
                        labelColor="textSecondary"
                        labelVariant="h3"
                        size="sm"
                        resize="vertical"
                        height="300px"
                        customErrorMessage={
                          fieldState.isTouched ? reconciliationCommentaryError?.message : undefined
                        }
                        column
                        subHeaderContent={
                          <Flex
                            borderColor="strokeDefault"
                            borderStyle="solid"
                            borderWidth="1px"
                            borderRadius="5px"
                            padding="15px"
                            flexDirection="column"
                            marginBottom="15px"
                            gap={4}
                            __dangerDoNotUseSx={{
                              backgroundColor: '#fff',
                            }}
                          >
                            <Typography variant="body1" color="textSecondary">
                              This appraisal is made:
                            </Typography>
                            <Controller
                              name="valueTypes"
                              control={form.control}
                              render={({ field }) => (
                                <RequiredFieldAnchor
                                  fieldName="valueTypes"
                                  isFieldComplete={Boolean(field.value?.length)}
                                >
                                  <RadioGroup
                                    name="valueType"
                                    items={VALUE_TYPE_ITEMS}
                                    onChange={(value) => {
                                      field.onChange([value]);
                                      handleChange();
                                    }}
                                    value={field.value?.[0] ?? ''}
                                    size="md"
                                    textVariant="h5"
                                    gap="8px"
                                  />
                                </RequiredFieldAnchor>
                              )}
                            />
                          </Flex>
                        }
                      />
                    )}
                  />
                </Flex>
                <Flex flex={1} flexDirection="column">
                  <Controller
                    name="highestAndBestUseCommentary"
                    control={form.control}
                    render={({ fieldState }) => (
                      <FormTextarea
                        name="highestAndBestUseCommentary"
                        requiredFieldName="highestAndBestUseCommentary"
                        control={form.control}
                        label="Highest & Best Use Commentary"
                        placeholder={isMockOrder ? 'Optional' : 'Required'}
                        isRequired={!isMockOrder}
                        height="300px"
                        labelWidth="250px"
                        inputWidth="500px"
                        labelColor="textSecondary"
                        labelVariant="h3"
                        size="sm"
                        resize="vertical"
                        customErrorMessage={
                          fieldState.isTouched
                            ? highestAndBestUseCommentaryError?.message
                            : undefined
                        }
                        column
                      />
                    )}
                  />
                </Flex>
              </Flex>
            </Flex>
          </Flex>
        </Form>
      </FormProvider>
    </Flex>
  );
};
